/**
 * The Analyst Workbench's browser storage, kept free of screen imports so the shell's sign-out and the auth module
 * can clear it without loading the Workbench (AIC-404). Every Workbench key starts with this prefix; nothing else in
 * the portal uses it.
 */
import { clearPracticeMemory } from "../../practice/practiceStore.ts";

export const WORKBENCH_STORAGE_PREFIX = "swt.workbench.";
const KEY_PREFIX = `${WORKBENCH_STORAGE_PREFIX}v2:`;

const normalized = (email: string) => email.trim().toLowerCase();

/** One person's key: normalized email and BUILD/live. */
export function workbenchKey(email: string, build: boolean): string {
  return `${KEY_PREFIX}${JSON.stringify([normalized(email), build ? "build" : "live"])}`;
}

/** Removes every Workbench key from this browser: on sign-out, so the next user of a shared machine sees none of it. */
export function clearWorkbenchStorage(storage: Storage | null = safeLocalStorage()): void {
  removeWhere(storage, () => true);
}

/**
 * The backup to the sign-out clear, run once the signed-in user is known (never in preview): removes every Workbench
 * key that isn't this person's own v2 key, in either mode, including any other account's and any older format.
 */
export function pruneOtherWorkbenchStorage(email: string, storage: Storage | null = safeLocalStorage()): void {
  if (!email.trim()) return;
  const own = new Set([workbenchKey(email, true), workbenchKey(email, false)]);
  removeWhere(storage, (key) => !own.has(key));
}

/** Other per-person presentation state: portfolio views and scroll, review notes (AIC-348), the active practice session (AIC-463). Cleared on sign-out too. */
export const PORTAL_VIEW_STORAGE_PREFIXES = ["swtracker.portfolio.", "swtracker.review.notes.", "swtracker.practice."] as const;

/** Sign-out clear for the Workbench and every other per-person browser key the portal keeps. */
export function clearPortalStorage(storage: Storage | null = safeLocalStorage(), session: Storage | null = safeSessionStorage()): void {
  clearPracticeMemory();
  removeWhere(storage, () => true);
  removeWhere(storage, () => true, PORTAL_VIEW_STORAGE_PREFIXES);
  // Review-note drafts live in sessionStorage.
  removeWhere(session, () => true, PORTAL_VIEW_STORAGE_PREFIXES);
}

function safeSessionStorage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function removeWhere(storage: Storage | null, remove: (key: string) => boolean, prefixes: readonly string[] = [WORKBENCH_STORAGE_PREFIX]): void {
  if (!storage) return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key && prefixes.some((p) => key.startsWith(p)) && remove(key)) keys.push(key);
    }
    for (const key of keys) storage.removeItem(key);
  } catch {
    // Storage unavailable: there is nothing this browser kept.
  }
}

function safeLocalStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}
